import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SanctionLetterPage.css';

export default function SanctionLetterPage() {
    const navigate = useNavigate();
    const iframeRef = useRef(null);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef(null);

    const [letter, setLetter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [sendMessage, setSendMessage] = useState('');
    const [canViewSanctionLetter, setCanViewSanctionLetter] = useState(true);

    const [signature, setSignature] = useState('');
    const [savingSignature, setSavingSignature] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession') || '{}');
        const requestHeaders = session?.token ? { Authorization: `Bearer ${session.token}` } : undefined;

        const load = async () => {
            if (!session?.userId) {
                setError('Session expired. Please login again.');
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const statsRes = await fetch(`${API_URL}/api/dashboard/all-data/${session.userId}`, {
                    headers: requestHeaders
                });
                const statsData = await statsRes.json().catch(() => ({}));
                const activeLoans = Number(statsData?.stats?.activeLoans || 0);
                const adminApprovedLoans = Number(statsData?.stats?.adminApprovedLoans || 0);
                // Allow if active or Admin Approved
                const isActive = activeLoans > 0 || adminApprovedLoans > 0;
                setCanViewSanctionLetter(isActive);
                if (!isActive) {
                    setLetter(null);
                    setError('Sanction letter will be available once your loan becomes Approved or Active.');
                    return;
                }

                const res = await fetch(`${API_URL}/api/sanction-letter/me`, {
                    credentials: 'include',
                    headers: requestHeaders
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.success) {
                    throw new Error(data.message || 'Unable to load sanction letter');
                }
                const localSignature = localStorage.getItem(`sanctionLetterSignature_${session.userId}`) || '';
                if (data.letter?.signatureImage) {
                    setSignature(data.letter.signatureImage);
                } else if (localSignature) {
                    setSignature(localSignature);
                }
                setLetter(data.letter);
                setError('');
            } catch (err) {
                setError(err.message || 'Unable to load sanction letter');
                setLetter(null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [API_URL]);

    const handlePrint = () => {
        const frame = iframeRef.current;
        if (!frame?.contentWindow) return;
        frame.contentWindow.focus();
        frame.contentWindow.print();
    };

    const handleSendEmail = async () => {
        const session = JSON.parse(localStorage.getItem('userSession') || '{}');
        setSendingEmail(true);
        setSendMessage('');
        try {
            const res = await fetch(`${API_URL}/api/sanction-letter/me/send-email`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {})
                },
                body: JSON.stringify({})
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                throw new Error(data.message || 'Unable to send email');
            }
            setSendMessage(data.message || 'Sanction letter sent successfully.');
        } catch (err) {
            setSendMessage(err.message || 'Unable to send email');
        } finally {
            setSendingEmail(false);
        }
    };

    const getPoint = useCallback((event) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const clientX = event.touches?.[0]?.clientX ?? event.clientX;
        const clientY = event.touches?.[0]?.clientY ?? event.clientY;
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    }, []);

    const drawLine = useCallback((from, to) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.lineWidth = 2.6;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#111827';
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
    }, []);

    const ensureCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const parent = canvas.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const width = Math.max(320, Math.floor(rect.width));
        const height = 160;
        const ratio = window.devicePixelRatio || 1;
        canvas.width = width * ratio;
        canvas.height = height * ratio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            ctx.clearRect(0, 0, width, height);
        }
    }, []);

    useEffect(() => {
        if (!letter?.signatureImage) {
            ensureCanvas();
            const handleResize = () => ensureCanvas();
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [ensureCanvas, letter]);

    const beginDraw = useCallback((event) => {
        event.preventDefault();
        const point = getPoint(event);
        if (!point) return;
        drawingRef.current = true;
        lastPointRef.current = point;
        setError('');
    }, [getPoint]);

    const moveDraw = useCallback((event) => {
        if (!drawingRef.current) return;
        event.preventDefault();
        const point = getPoint(event);
        if (!point || !lastPointRef.current) return;
        drawLine(lastPointRef.current, point);
        lastPointRef.current = point;
        setSignature(canvasRef.current?.toDataURL('image/png') || '');
    }, [drawLine, getPoint]);

    const endDraw = useCallback(() => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        lastPointRef.current = null;
        const image = canvasRef.current?.toDataURL('image/png') || '';
        if (image) setSignature(image);
    }, []);

    const clearSignature = () => {
        ensureCanvas();
        setSignature('');
    };

    const confirmSign = async () => {
        if (!signature) {
            setError('Please sign before saving.');
            return;
        }
        setSavingSignature(true);
        const session = JSON.parse(localStorage.getItem('userSession') || '{}');
        try {
            const response = await fetch(`${API_URL}/api/sanction-letter/me`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(session?.token ? { Authorization: `Bearer ${session.token}` } : {})
                },
                body: JSON.stringify({
                    meta: { signatureImage: signature },
                    signatureImage: signature
                })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Unable to save signature');
            }
            localStorage.setItem(`sanctionLetterSignature_${session.userId}`, signature);
            setLetter(data.letter || { ...letter, signatureImage: signature });
            setSendMessage('Signature saved successfully!');
        } catch (err) {
            setError(err.message || 'Unable to save signature');
        } finally {
            setSavingSignature(false);
        }
    };

    const previewHtml = letter?.html
        ? (
            signature && !letter.html.includes('Electronically Signed')
                ? letter.html.replace(
                    /<span style="opacity:\.35;">Sign here<\/span>|<div class="signature-slot"><\/div>/i,
                    `<div style="color: #059669; font-weight: bold; font-family: monospace; border: 2px dashed #059669; padding: 8px; text-align: center; border-radius: 4px;">Verified & Electronically Signed<br/>${new Date().toLocaleDateString()}</div>`
                )
                : letter.html
        )
        : '';

    const needsSignature = letter && !letter.signatureImage;

    return (
        <div className="sl-root">
            <div className="sl-header">
                <div>
                    <div className="sl-kicker">Loan Document</div>
                    <h1>Sanction Letter & eSign</h1>
                    <p>Review your sanctioned loan letter and complete Aadhaar eSign.</p>
                </div>
                {!needsSignature && letter && (
                    <div className="sl-actions">
                        <button className="sl-btn" onClick={handlePrint} disabled={!letter}>
                            Print Letter
                        </button>
                        <button className="sl-btn sl-btn-primary" onClick={handleSendEmail} disabled={!letter || sendingEmail}>
                            {sendingEmail ? 'Sending...' : 'Send to Email'}
                        </button>
                    </div>
                )}
            </div>

            {letter && !needsSignature && (
                <div className="sl-meta">
                    <span>Company: {letter.companyName}</span>
                    <span>Lender: {letter.lenderName}</span>
                    <span>Borrower: {letter.borrowerName}</span>
                    <span>Loan: {letter.loanReference}</span>
                </div>
            )}

            {error && <div className="sl-error">{error}</div>}
            {sendMessage && <div className="sl-info">{sendMessage}</div>}

            <div className="sl-preview-shell">
                {loading ? (
                    <div className="sl-loading">
                        <div className="sl-spinner" />
                        <span>Loading sanction letter…</span>
                    </div>
                ) : !canViewSanctionLetter ? (
                    <div className="sl-empty">
                        <h3>Sanction letter locked</h3>
                        <p>Your loan is not Approved yet. Once approved, this tab will be available.</p>
                        <button className="sl-btn" onClick={() => navigate('/dashboard/loans')} style={{marginTop: '1rem'}}>
                            Go to My Loans
                        </button>
                    </div>
                ) : needsSignature ? (
                    <div className="sl-esign-grid" style={{ padding: '2rem', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', maxWidth: '600px', margin: '0 auto' }}>
                        <div className="sl-esign-signature">
                            <h3 style={{ marginBottom: '0.5rem' }}>Electronic Signature</h3>
                            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Please read and accept the terms of the Sanction Letter to proceed.</p>
                            
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <input
                                    type="checkbox"
                                    checked={!!signature}
                                    onChange={(e) => setSignature(e.target.checked ? 'ELECTRONICALLY_SIGNED' : '')}
                                    style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#3b82f6' }}
                                />
                                <span style={{ fontSize: '15px', color: '#334155', lineHeight: '1.5' }}>
                                    I hereby acknowledge and agree to the terms and conditions outlined in this Sanction Letter. I authorize FinMudra to process my loan application based on these terms.
                                </span>
                            </label>

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button className="sl-btn sl-btn-primary" onClick={confirmSign} disabled={!signature || savingSignature} style={{ padding: '12px 24px' }}>
                                    {savingSignature ? 'Saving…' : 'Accept & Submit'}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : letter?.html ? (
                    <iframe
                        ref={iframeRef}
                        title="Sanction Letter"
                        className="sl-frame"
                        srcDoc={previewHtml}
                    />
                ) : (
                    <div className="sl-empty">
                        <h3>No sanction letter yet</h3>
                        <p>We could not find a loan record to generate the letter from.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
